import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createGameComment, createComment } from '~/api';
import { CommentSort, CreateCommentRequest } from '~/types';
import { CreateGameCommentRequest } from '~/types/missionGameApi';
import commentKey from './commentKey';
import useUpdateCommentList from './useUpdateCommentList';
import useUpdateGameCommentList from './useUpdateGameCommentList';

export const useCreateCommentMutation = (sort_by: CommentSort) => {
  const queryClient = useQueryClient();
  const { updatePersonaDetailsTotalComment } = useUpdateCommentList();

  return useMutation({
    mutationFn: (newComment: CreateCommentRequest) => createComment(newComment),
    onSuccess: ({ status }, request) => {
      const sortReverse: CommentSort = sort_by === 'newest' ? 'top' : 'newest';

      // Invalidate current sort
      queryClient.invalidateQueries({
        queryKey: commentKey.getCommentList({
          character_id: request.character_id,
          sort_by,
        }),
      });

      // Check if the reverse sort query is already cached before invalidating
      const reverseQueryKey = commentKey.getCommentList({
        character_id: request.character_id,
        sort_by: sortReverse,
      });

      if (queryClient.getQueryData(reverseQueryKey)) {
        queryClient.invalidateQueries({ queryKey: reverseQueryKey });
      }

      updatePersonaDetailsTotalComment(
        {
          character_id: request.character_id,
        },
        'create',
      );
    },
    onError: (error) => {
      console.error(error);
      console.error('Error creating comment:', error);
    },
  });
};

export const useCreateGameCommentMutation = (sort_by: CommentSort) => {
  const queryClient = useQueryClient();
  const { updateGameDetailsTotalComment } = useUpdateGameCommentList();

  return useMutation({
    mutationFn: (newComment: CreateGameCommentRequest) =>
      createGameComment(newComment),
    onSuccess: ({ data, status }, request) => {
      const sortReverse: CommentSort = sort_by === 'newest' ? 'top' : 'newest';

      // Invalidate current sort
      queryClient.invalidateQueries({
        queryKey: commentKey.getGameCommentList({
          game_id: request.game_id,
          sort_by,
        }),
      });

      // Check if the reverse sort query is already cached before invalidating
      const reverseQueryKey = commentKey.getGameCommentList({
        game_id: request.game_id,
        sort_by: sortReverse,
      });

      if (queryClient.getQueryData(reverseQueryKey)) {
        queryClient.invalidateQueries({ queryKey: reverseQueryKey });
      }

      updateGameDetailsTotalComment(
        {
          game_id: request.game_id,
        },
        'create',
      );
    },
    onError: (error) => {
      console.error('Error creating comment:', error);
    },
  });
};
